import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FormEvent, ReactElement, ReactNode } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import {
  isQuantity,
  majorStringFromMinor,
  minorFromMajorString,
  subQuantity,
} from '@/domain/money'
import { slugSymbol } from '@/domain/private-holding'
import type {
  Disposal,
  EffectiveValuation,
  HoldingEvent,
  ManualAccount,
  ManualPosition,
  PrivateHoldingRow,
  ValuationRow,
} from '~/api/client'
import { api, formatMinor, formatQuantity } from '~/api/client'
import { chartCss } from '~/charts/theme'
import { EntityChoice, EntityName } from '~/components/EntityChoice'
import { WarrantGrantDialog } from '~/components/WarrantGrant'
import { Card, CardHead } from '~/components/ui/Card'
import { Button, Filter } from '~/components/ui/Controls'
import { Modal as SystemModal } from '~/components/ui/Modal'
import { StatusPill } from '~/components/ui/Status'
import { DataTable } from '~/components/ui/Table'
import {
  collectionControl,
  useCollectionStanding,
  useRememberCollection,
  useRequestCollection,
  useSavedCollection,
} from '~/state/collections'
import { describeAge, today } from '~/state/dates'
import { CONFIDENCE_TONE } from '~/state/private-holdings'
import { useSettings } from '~/state/queries'

type AccountKind = ManualAccount['kind']
type InstrumentKind = ManualPosition['instrument']['kind']
type Instrument = ManualPosition['instrument']
type Confidence = ValuationRow['confidence']

const ACCOUNT_KINDS: AccountKind[] = ['cash', 'brokerage', 'wallet', 'private']
const INSTRUMENT_KINDS: InstrumentKind[] = ['cash', 'equity', 'etf', 'fund', 'crypto', 'private']
const CONFIDENCES: Confidence[] = ['low', 'medium', 'high']

/** The one kind of holding whose price comes from a valuation rather than a feed. */
const PRIVATE: InstrumentKind = 'private'

/**
 * A menu nobody has answered yet.
 *
 * Both kind menus used to open on `private` — the fourth of four, and the one
 * kind that only exists for a household holding shares in an unlisted company.
 * A household adding a savings account had to take two menus off an answer it
 * never gave, and met refusals written in that answer's vocabulary on the way.
 * The entity menu beside it opens unanswered and the wallet form in Sources
 * says why: one menu that is always answered cannot be the one that quietly
 * defaults.
 *
 * It is the empty string because that is what an unselected `option` submits,
 * so the draft holds what the control holds rather than a second spelling of
 * the same absence.
 */
const UNCHOSEN = ''
type ChosenAccountKind = AccountKind | typeof UNCHOSEN
type ChosenInstrumentKind = InstrumentKind | typeof UNCHOSEN

/*
 * The dialogs are the system's modal, and everything inside one is the system's
 * too — except the fields, which wrap their control rather than pointing at it
 * by id. That is what these label rules are for, and why they are scoped to the
 * fieldsets rather than written on `label`.
 */
const CSS = `
.nw-manual p.nw-hint { color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-4); }
.nw-manual .nw-holding { border-top: var(--nw-hairline-width) solid var(--nw-row-rule); padding: var(--nw-space-4) 0 var(--nw-space-2); }
.nw-manual .nw-holding:first-of-type { border-top: 0; padding-top: var(--nw-space-2); }
.nw-manual .nw-head { align-items: baseline; display: flex; flex-wrap: wrap; gap: var(--nw-space-2) var(--nw-space-3); justify-content: space-between; }
.nw-manual .nw-head h3 { font-size: var(--nw-text-title); margin: 0; }
.nw-manual .nw-meta { color: var(--nw-muted); font-size: var(--nw-text-meta); letter-spacing: var(--nw-track-label); margin: var(--nw-space-1) 0 var(--nw-space-3); text-transform: uppercase; }
.nw-manual fieldset { border: 0; display: grid; gap: var(--nw-space-3); grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); margin: 0 0 var(--nw-space-3); padding: 0; }
.nw-manual fieldset label { color: var(--nw-text); display: flex; flex-direction: column; font-size: var(--nw-text-cell); font-weight: var(--nw-weight-medium); gap: var(--nw-space-2); }
.nw-manual fieldset :where(input, select) { width: 100%; }
.nw-manual fieldset label.nw-check { align-items: center; flex-direction: row; font-size: var(--nw-text-body); font-weight: var(--nw-weight-body); letter-spacing: 0; text-transform: none; }
.nw-manual fieldset label.nw-check input { width: auto; }
.nw-manual .nw-error {
  background: color-mix(in srgb, var(--nw-critical) 12%, transparent);
  border: var(--nw-hairline-width) solid var(--nw-loss-edge); border-radius: var(--nw-radius-md);
  font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-3); padding: 9px 12px;
}
.nw-manual .nw-actions { display: flex; flex-wrap: wrap; gap: var(--nw-space-2); }
.nw-manual .nw-entered p { color: var(--nw-body-ink); font-size: var(--nw-text-cell); line-height: 1.55; margin: 0 0 var(--nw-space-3); max-width: 62ch; }
.nw-manual .nw-entered .nw-entered-note { color: var(--nw-muted); }
.nw-manual .nw-entered .nw-actions { justify-content: flex-end; margin-top: var(--nw-space-4); }
/* The last row of a dialog's form is its footer; the modal's own foot slot sits
   outside the form, where a submit button would not reach it. */
.nw-manual .nw-modal-body form > .nw-actions:last-child { border-top: var(--nw-hairline-width) solid var(--nw-hairline); justify-content: flex-end; margin-top: var(--nw-space-2); padding-top: var(--nw-space-4); }
.nw-manual .nw-none { color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-3); }
.nw-manual .nw-confirm { color: var(--nw-critical); font-size: var(--nw-text-cell); }
.nw-manual small { color: var(--nw-muted); }
.nw-manual code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: var(--nw-text-chip); }
.nw-manual .nw-drafted { border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-radius-md); list-style: none; margin: 0 0 var(--nw-space-3); padding: 0; }
.nw-manual .nw-drafted li { align-items: center; border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule); display: flex; flex-wrap: wrap; gap: var(--nw-space-2); justify-content: space-between; padding: var(--nw-space-2) var(--nw-space-3); }
.nw-manual .nw-drafted li:last-child { border-bottom: 0; }
.nw-manual .nw-named { font-size: var(--nw-text-cell); }
`

const POSITION_COLUMNS = [
  { id: 'instrument', label: 'Instrument' },
  { id: 'quantity', label: 'Quantity', width: '160px', align: 'right' as const },
  { id: 'since', label: 'Since', width: '130px' },
  { id: 'valued', label: 'Valued at', width: '240px' },
  { id: 'actions', label: ' ', width: '240px' },
]

const VALUATION_COLUMNS = [
  { id: 'date', label: 'Date', width: '120px' },
  { id: 'instrument', label: 'Instrument', width: '140px' },
  { id: 'round', label: 'Round', width: '150px' },
  { id: 'shares', label: 'Shares', width: '130px', align: 'right' as const },
  { id: 'price', label: 'Price / share', width: '140px', align: 'right' as const },
  { id: 'haircut', label: 'Haircut', width: '100px', align: 'right' as const },
  { id: 'source', label: 'Source' },
  { id: 'actions', label: ' ', width: '120px' },
]

interface ModalProps {
  title: string
  onClose: () => void
  children: ReactNode
}

/**
 * A form that takes the page over until it is answered or dismissed.
 *
 * The native element is what makes that true rather than merely painted: the
 * rest of the page stops taking keys and pointer events, Escape closes it, and
 * focus cannot wander out behind it. A div with a shadow claims all three and
 * has none of them, and this repository has no component library to borrow them
 * from.
 */
function Modal({ title, onClose, children }: ModalProps): ReactElement {
  const headingId = useId()

  useEffect(() => {
    const opener = document.activeElement
    // Whatever opened the dialog gets the focus back, which the element only
    // does for itself when it is the one doing the closing — this one is closed
    // by being taken off the page.
    return () => {
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [])

  return (
    <SystemModal title={title} labelledBy={headingId} onClose={onClose}>
      {children}
    </SystemModal>
  )
}

export function Manual(): ReactElement {
  return (
    <main className="nw nw-manual">
      <style>{chartCss + CSS}</style>
      <h1>Manual entries</h1>
      <p className="nw-hint">
        Everything here is typed by hand and collected like any other source, so a holding nobody has
        automated still reaches the total.
      </p>
      <SavedCollection />
      <ManualAccounts />
      <ValuationHistory />
    </main>
  )
}

/** The two things a household holds that no source will ever report for it. */
type EntryKind = 'holding' | 'grant'

const ENTRY_CHOICES = [
  { id: 'holding', label: 'A holding' },
  { id: 'grant', label: 'A warrant grant' },
]

/**
 * Hand entry on its own, opened from the app bar instead of from this page.
 * Hand entry is how a holding got here rather than a kind of holding, so the
 * form has to be within reach wherever somebody is standing when they remember
 * one.
 *
 * A warrant grant is the second thing that only reaches the product this way,
 * and it is offered beside the first rather than from the page that reports on
 * grants: the report is where they are read, and a grant has to be enterable by
 * somebody who has never been to it.
 */
export function AddHoldingDialog({ onClose }: { onClose: () => void }): ReactElement {
  const [kind, setKind] = useState<EntryKind>('holding')
  const switcher = (
    <Filter
      label="Adding"
      choices={ENTRY_CHOICES}
      value={kind}
      onChange={(id) => setKind(id as EntryKind)}
    />
  )

  if (kind === 'grant') return <WarrantGrantDialog switcher={switcher} onClose={onClose} />
  return <AddAccountDialog switcher={switcher} onClose={onClose} />
}

/**
 * It waits for what is already entered before offering the form: the address an
 * account is filed under is worked out from its name against the keys already
 * taken, and a name checked against a list that has not arrived yet derives a
 * key that belongs to an existing account — which the save would then replace.
 */
function AddAccountDialog({
  switcher,
  onClose,
}: {
  switcher: ReactNode
  onClose: () => void
}): ReactElement {
  const source = useQuery({ queryKey: ['manual'], queryFn: () => api.manualSource() })
  const baseCurrency = useBaseCurrency()

  return (
    <div className="nw-manual">
      <style>{chartCss + CSS}</style>
      {source.data === undefined ? (
        <Modal title="New account" onClose={onClose}>
          {switcher}
          {source.error ? (
            <p className="nw-error">
              Could not read what is already entered: {source.error.message}
            </p>
          ) : (
            <p className="nw-none">Loading…</p>
          )}
        </Modal>
      ) : (
        <AccountDialog
          initial={emptyAccount(baseCurrency)}
          taken={source.data.accounts.map((account) => account.stableKey)}
          switcher={switcher}
          onClose={onClose}
        />
      )}
    </div>
  )
}

/**
 * What a hand-entry has and has not done yet.
 *
 * A holding entered by hand becomes a position when a collection runs, and
 * every total and every gated tab reads positions — so the form used to close
 * onto a page still saying nothing had been collected, which reads as a denial
 * of what the household just did rather than as a wait. The figures are right
 * and the silence was not.
 *
 * The control is the one the Overview already offers rather than a second way
 * in, because a hand-entry must not become a collection path of its own. Where
 * a deployment has no queue to ask, it says so instead of offering a button
 * that cannot work — which is the same sentence it shows anywhere else, so this
 * makes no claim about the runtime it is running on.
 */
function AfterHandEntry({ name, onClose }: { name: string; onClose: () => void }): ReactElement {
  const standing = useCollectionStanding()
  const request = useRequestCollection()
  const control = collectionControl(standing.data)
  const saved = useSavedCollection()

  // The save collected it already. Offering the control as well would ask the
  // household to do what has just been done, behind a floor that then said no.
  if (saved?.started === true) {
    return (
      <div className="nw-entered">
        <p role="status">
          <b>{name}</b> is saved. {saved.note}
        </p>
        <div className="nw-actions">
          <Button kind="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="nw-entered">
      <p role="status">
        <b>{name}</b> is saved. {saved?.note ??
          'It reaches your figures — and any tab that needs them — on the next collection.'}
      </p>
      {control.note !== '' && <p className="nw-entered-note">{control.note}</p>}
      {request.error && <p className="nw-error">{request.error.message}</p>}
      <div className="nw-actions">
        <Button onClick={onClose}>Done</Button>
        <Button
          kind="primary"
          disabled={control.disabled || request.isPending}
          onClick={() => request.mutate()}
        >
          {control.label}
        </Button>
      </div>
    </div>
  )
}

/**
 * What an account and a position hold that this form has no control for.
 *
 * A save replaces the account wholesale, so a field the body leaves out is a
 * field the save destroys — and this form edits four where the account has
 * more. They are named by what the form does edit rather than by what it must
 * keep, so a field added to an account survives an edit without anyone
 * remembering to name it here.
 */
type CarriedAccount = Omit<
  ManualAccount,
  'stableKey' | 'entityKey' | 'name' | 'kind' | 'currency' | 'liquid' | 'positions'
>
type CarriedPosition = Omit<
  ManualPosition,
  'instrument' | 'quantity' | 'costBasisMinor' | 'costBasisCurrency' | 'asOf'
>

interface PositionDraft {
  kind: ChosenInstrumentKind
  symbol: string
  name: string
  currency: string
  isin: string
  quantity: string
  costBasisMajor: string
  /**
   * Asked for rather than assumed. A basis is often paid in the currency the
   * instrument is quoted in and sometimes is not, and nothing downstream can
   * tell the two apart, so the one person who knows is the one who is asked.
   */
  costBasisCurrency: string
  asOf: string
  /** Absent on a position being typed for the first time, which holds nothing yet. */
  carried?: CarriedPosition
}

interface AccountDraft {
  /**
   * The address the account is already saved under. Absent until it has one,
   * which is what tells a save whether to derive a key or leave it alone.
   */
  stableKey?: string
  entityKey: ManualAccount['entityKey']
  name: string
  kind: ChosenAccountKind
  currency: string
  liquid: boolean
  positions: PositionDraft[]
  /** Absent on an account being described for the first time, which holds nothing yet. */
  carried?: CarriedAccount
}

const emptyPosition = (currency: string): PositionDraft => ({
  kind: UNCHOSEN,
  symbol: '',
  name: '',
  currency,
  isin: '',
  quantity: '',
  costBasisMajor: '',
  costBasisCurrency: '',
  asOf: today(),
})

const emptyAccount = (currency: string): AccountDraft => ({
  entityKey: '',
  name: '',
  kind: UNCHOSEN,
  currency,
  liquid: false,
  positions: [],
})

/**
 * What a new account is denominated in before anybody types over it, which is
 * the household's own currency rather than a currency this file picked — the
 * same answer `emptyGrantDraft` takes as a parameter. Empty while the settings
 * query is still out, because a currency named here to fill the gap is one the
 * household has to notice and correct.
 */
function useBaseCurrency(): string {
  const stored = useSettings().data?.settings['baseCurrency']
  return typeof stored === 'string' ? stored.trim().toUpperCase() : ''
}

function draftOf(account: ManualAccount): AccountDraft {
  const {
    stableKey: _stableKey,
    entityKey: _entityKey,
    name: _name,
    kind: _kind,
    currency: _currency,
    liquid: _liquid,
    positions: _positions,
    ...carried
  } = account

  return {
    stableKey: account.stableKey,
    entityKey: account.entityKey,
    name: account.name,
    kind: account.kind,
    currency: account.currency,
    liquid: account.liquid,
    carried,
    positions: account.positions.map((p) => {
      const {
        instrument: _instrument,
        quantity: _quantity,
        costBasisMinor: _costBasisMinor,
        costBasisCurrency: _costBasisCurrency,
        asOf: _asOf,
        ...carriedPosition
      } = p
      return {
        kind: p.instrument.kind,
        symbol: p.instrument.symbol,
        name: p.instrument.name ?? '',
        currency: p.instrument.currency,
        isin: p.instrument.isin ?? '',
        quantity: p.quantity,
        costBasisMajor: p.costBasisMinor === undefined ? '' : majorStringFromMinor(p.costBasisMinor),
        // Blank where a basis was recorded before anything asked what it was
        // paid in, which is the prompt to say so rather than a field to skip.
        costBasisCurrency: p.costBasisCurrency ?? '',
        asOf: p.asOf ?? '',
        carried: carriedPosition,
      }
    }),
  }
}

/**
 * The address an account is saved at and matched to itself by on every later
 * run, derived from its name rather than asked for. It used to be a required
 * box on this form, which put an implementation detail ahead of the first thing
 * anyone knows about an account, and let two accounts be given one address.
 */
function deriveStableKey(name: string, taken: readonly string[]): string {
  const base = slugSymbol(name).toLowerCase()
  if (!taken.includes(base)) return base
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${base}-${suffix}`
    if (!taken.includes(candidate)) return candidate
  }
}

/**
 * Why this account cannot be saved yet, in the words of whoever would have
 * refused it. The nightly matches accounts by key and the server refuses the
 * contradictions, but a refusal that arrives as a failed request names a field
 * the person can no longer see.
 */
function refuseAccount(draft: AccountDraft): string | undefined {
  if (draft.name.trim() === '') return 'a name is required'
  if (draft.entityKey === '') return 'an entity is required'
  // Ahead of every rule written in the vocabulary of a particular kind: a menu
  // nobody has answered is asked, not told it answered wrongly.
  if (draft.kind === UNCHOSEN) return 'an account kind is required'
  if (draft.currency.trim().length !== 3) return 'a three-letter currency is required'
  if (draft.kind === PRIVATE && draft.liquid) return 'a private account cannot be liquid'
  if (draft.kind !== PRIVATE && draft.positions.some((one) => one.kind === PRIVATE)) {
    // The position dialog asks this too, but only of a position added after the
    // account kind was settled. Either menu can be answered last, and the
    // contradiction is the same whichever of them moved.
    return 'a private holding belongs in a private account'
  }
  if (draft.positions.length === 0) {
    return 'an account needs at least one position — add one before saving'
  }
  return undefined
}

function refusePosition(draft: PositionDraft, accountKind: ChosenAccountKind): string | undefined {
  if (draft.kind === UNCHOSEN) return 'an instrument kind is required'
  if (draft.symbol.trim() === '') return 'a symbol is required'
  if (draft.currency.trim().length !== 3) return 'a three-letter currency is required'
  if (!isQuantity(draft.quantity.trim())) return 'a quantity is required, as a decimal number'
  // Only against an account kind that has been answered. Refusing a holding for
  // sitting in the wrong kind of account, while the account's own menu still
  // reads "Choose…", names a decision nobody has made; the account's refusal
  // catches it at the save either way.
  if (accountKind !== UNCHOSEN && accountKind !== PRIVATE && draft.kind === PRIVATE) {
    return 'a private holding belongs in a private account'
  }
  if (draft.costBasisMajor.trim() !== '') {
    try {
      minorFromMajorString(draft.costBasisMajor.trim())
    } catch (error) {
      return `cost basis: ${(error as Error).message}`
    }
    // A basis is subtracted from a value already converted into the household's
    // base currency, so one that does not say what it was paid in cannot be
    // subtracted at all. Asking here is the only moment anybody still knows.
    if (draft.costBasisCurrency.trim().length !== 3) {
      return 'a cost basis needs the three-letter currency it was paid in'
    }
  }
  return undefined
}

function toInstrument(draft: PositionDraft): Instrument {
  // Unreachable behind `refusePosition`, and a refusal rather than a fallback
  // because a kind chosen here would be saved under the household's name
  // without the household ever having said it.
  if (draft.kind === UNCHOSEN) throw new Error('an instrument kind is required')
  const symbol = draft.symbol.trim().toUpperCase()
  return {
    kind: draft.kind,
    symbol,
    // Blank sends no name at all. Filling it in with the ticker here was a
    // write rather than a display default: every resolver stamps this name onto
    // the household's instrument row, so a holding saved without one renamed
    // the security to its ticker on every page of theirs that lists it, and for
    // every other source of theirs reporting it. Left out, the row keeps the
    // name it has, and a row nobody has named yet gets the ticker from the
    // resolver instead.
    ...(draft.name.trim() === '' ? {} : { name: draft.name.trim() }),
    currency: draft.currency.trim().toUpperCase(),
    ...(draft.isin.trim() === '' ? {} : { isin: draft.isin.trim() }),
  }
}

function toManualAccount(draft: AccountDraft, stableKey: string): ManualAccount {
  // Unreachable behind `refuseAccount`, for the same reason `toInstrument`
  // refuses: the kind is the household's answer or it is nobody's.
  if (draft.kind === UNCHOSEN) throw new Error('an account kind is required')
  // What the form does not edit is spread first, so clearing a field it does
  // edit still clears it.
  return {
    ...draft.carried,
    stableKey,
    entityKey: draft.entityKey,
    name: draft.name.trim(),
    kind: draft.kind,
    currency: draft.currency.trim().toUpperCase(),
    liquid: draft.liquid,
    positions: draft.positions.map((p) => ({
      ...p.carried,
      instrument: toInstrument(p),
      quantity: p.quantity.trim(),
      ...(p.costBasisMajor.trim() === ''
        ? {}
        : {
            costBasisMinor: minorFromMajorString(p.costBasisMajor.trim()),
            costBasisCurrency: p.costBasisCurrency.trim().toUpperCase(),
          }),
      ...(p.asOf.trim() === '' ? {} : { asOf: p.asOf.trim() }),
    })),
  }
}

/** A position on screen, with everything a valuation of it needs already known. */
interface ValuationTarget {
  accountName: string
  entityKey: string
  instrument: Instrument
  quantity: string
}

/**
 * What a hand-entered save says on the page it closed onto: the collection it
 * started, from the server's own sentence.
 */
function SavedCollection(): ReactElement | null {
  const saved = useSavedCollection()
  // Watched here too, because the standing is what refreshes the figures on
  // this page once the collection a save started has finished.
  useCollectionStanding()
  if (saved === null) return null
  return (
    <p className="nw-hint" role="status">
      {saved.note}
    </p>
  )
}

function useInvalidateMoney(): (saved?: unknown) => Promise<void> {
  const client = useQueryClient()
  const remember = useRememberCollection()
  return async (saved?: unknown): Promise<void> => {
    remember(saved)
    await client.invalidateQueries({ queryKey: ['manual'] })
    await client.invalidateQueries({ queryKey: ['valuations'] })
    await client.invalidateQueries({ queryKey: ['holdings'] })
  }
}

function ManualAccounts(): ReactElement {
  const [editing, setEditing] = useState<AccountDraft | null>(null)
  const [valuing, setValuing] = useState<ValuationTarget | null>(null)
  const [disposing, setDisposing] = useState<DisposalTarget | null>(null)
  const [historyOf, setHistoryOf] = useState<HistoryTarget | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const invalidate = useInvalidateMoney()
  const baseCurrency = useBaseCurrency()

  const source = useQuery({ queryKey: ['manual'], queryFn: () => api.manualSource() })
  const asOf = today()
  const effective = useQuery({
    queryKey: ['valuations', 'effective', asOf],
    queryFn: () => api.effectiveValuations(asOf),
  })
  // The document has no instrument id of its own until the nightly resolves
  // one, and a disposal is filed against the holding rather than against the
  // account it happens to sit in.
  const holdings = useQuery({
    queryKey: ['holdings', 'private', asOf],
    queryFn: () => api.privateHoldings(asOf),
  })

  const remove = useMutation({
    mutationFn: (stableKey: string) => api.deleteManualAccount(stableKey),
    onSuccess: async (result) => {
      setConfirming(null)
      await invalidate(result)
    },
  })

  const accounts = source.data?.accounts ?? []

  return (
    <Card>
      <CardHead
        title="Hand-entered positions"
        lede="Dating a position tells the snapshot when it became true; an undated one counts from the beginning of history."
      />

      {source.isPending && <p>Loading…</p>}
      {source.error && (
        <p className="nw-error">Could not load manual entries: {source.error.message}</p>
      )}
      {remove.error && <p className="nw-error">{remove.error.message}</p>}

      {source.data && accounts.length === 0 && (
        <p className="nw-none">Nothing entered by hand yet.</p>
      )}

      {accounts.map((account) => (
        <article className="nw-holding" key={account.stableKey}>
          <div className="nw-head">
            <h3>{account.name}</h3>
            <div className="nw-actions">
              <Button onClick={() => setEditing(draftOf(account))}>Edit {account.name}</Button>
              {confirming === account.stableKey ? (
                <>
                  <span className="nw-confirm">Delete for good?</span>
                  <Button
                    kind="danger"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(account.stableKey)}
                  >
                    Delete
                  </Button>
                  <Button onClick={() => setConfirming(null)}>Keep</Button>
                </>
              ) : (
                <Button kind="danger" onClick={() => setConfirming(account.stableKey)}>
                  Delete {account.name}
                </Button>
              )}
            </div>
          </div>
          <p className="nw-meta">
            <EntityName entityKey={account.entityKey} /> · {account.kind} · {account.currency} ·{' '}
            <code>{account.stableKey}</code>
          </p>
          <Positions
            account={account}
            valuations={effective.data?.valuations ?? []}
            holdings={holdings.data?.holdings ?? []}
            onValue={setValuing}
            onDispose={setDisposing}
            onHistory={setHistoryOf}
          />
        </article>
      ))}

      <div className="nw-actions">
        <Button kind="primary" onClick={() => setEditing(emptyAccount(baseCurrency))}>
          Add account
        </Button>
      </div>

      {editing && (
        <AccountDialog
          initial={editing}
          taken={accounts.map((a) => a.stableKey)}
          onClose={() => setEditing(null)}
        />
      )}
      {valuing && <ValuationDialog target={valuing} onClose={() => setValuing(null)} />}
      {disposing && <DisposalDialog target={disposing} onClose={() => setDisposing(null)} />}
      {historyOf && <HistoryDialog target={historyOf} onClose={() => setHistoryOf(null)} />}
    </Card>
  )
}

function Positions({
  account,
  valuations,
  holdings,
  onValue,
  onDispose,
  onHistory,
}: {
  account: ManualAccount
  valuations: readonly EffectiveValuation[]
  holdings: readonly PrivateHoldingRow[]
  onValue: (target: ValuationTarget) => void
  onDispose: (target: DisposalTarget) => void
  onHistory: (target: HistoryTarget) => void
}): ReactElement {
  return (
    <DataTable columns={POSITION_COLUMNS} label={`Positions in ${account.name}`}>
        {account.positions.map((position) => {
          const holding = holdings.find(
            (row) => row.symbol.toUpperCase() === position.instrument.symbol.toUpperCase(),
          )
          const gone = position.disposals ?? []
          return (
          <tr key={position.instrument.symbol}>
            <td>
              <strong>{position.instrument.symbol}</strong> <small>{position.instrument.name}</small>
            </td>
            <td className="nw-num">
              {formatQuantity(holding?.units ?? position.quantity)}
              {gone.length > 0 && (
                <>
                  <br />
                  <small>of {formatQuantity(position.quantity)} held</small>
                </>
              )}
            </td>
            <td>{position.asOf ?? '—'}</td>
            <td>
              <InForce instrument={position.instrument} valuations={valuations} />
            </td>
            <td>
              {position.instrument.kind === PRIVATE && (
                <span className="nw-actions">
                  <Button
                    onClick={() =>
                      onValue({
                        accountName: account.name,
                        entityKey: account.entityKey,
                        instrument: position.instrument,
                        quantity: holding?.units ?? position.quantity,
                      })
                    }
                  >
                    Value {position.instrument.symbol}
                  </Button>
                  {holding !== undefined && (
                    <Button
                      onClick={() =>
                        onDispose({
                          instrumentId: holding.instrumentId,
                          symbol: holding.symbol,
                          name: holding.name,
                          unitLabel: holding.unitLabel,
                          currency: holding.currency,
                          units: holding.units,
                          heldSince: position.asOf,
                          disposals: holding.disposals,
                        })
                      }
                    >
                      Reduce {position.instrument.symbol}
                    </Button>
                  )}
                  {holding !== undefined && (
                    <Button
                      onClick={() =>
                        onHistory({
                          instrumentId: holding.instrumentId,
                          symbol: holding.symbol,
                          name: holding.name,
                          unitLabel: holding.unitLabel,
                          currency: holding.currency,
                        })
                      }
                    >
                      History of {position.instrument.symbol}
                    </Button>
                  )}
                </span>
              )}
            </td>
          </tr>
          )
        })}
    </DataTable>
  )
}

/**
 * What the holding is priced at today. The symbol is the only identity the two
 * sides share here — a hand-entered position has no instrument id until the
 * nightly has resolved one for it — and the write upper-cases it, so the match
 * is made on the same spelling that was sent.
 */
function InForce({
  instrument,
  valuations,
}: {
  instrument: Instrument
  valuations: readonly EffectiveValuation[]
}): ReactElement {
  if (instrument.kind !== PRIVATE) return <>Priced by its source</>

  const valuation = valuations.find(
    (candidate) => candidate.symbol.toUpperCase() === instrument.symbol.toUpperCase(),
  )
  if (!valuation) return <small>Not valued yet, so it counts for nothing.</small>

  return (
    <>
      {formatMinor(valuation.effectivePriceMinor, valuation.currency, {
        maximumFractionDigits: 2,
      })}{' '}
      <StatusPill tone={CONFIDENCE_TONE[valuation.confidence]} label={valuation.confidence} />
      <br />
      <small>
        {valuation.date}, {describeAge(valuation.ageDays)}
      </small>
    </>
  )
}

interface PositionEdit {
  /** Which position is being edited, or absent while one is being added. */
  index?: number
  draft: PositionDraft
}

function AccountDialog({
  initial,
  taken,
  switcher,
  onClose,
}: {
  initial: AccountDraft
  taken: readonly string[]
  /** The choice of what is being added, where this dialog is one of two. */
  switcher?: ReactNode
  onClose: () => void
}): ReactElement {
  const [draft, setDraft] = useState<AccountDraft>(initial)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [editing, setEditing] = useState<PositionEdit | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const invalidate = useInvalidateMoney()

  const save = useMutation({
    mutationFn: (account: ManualAccount) => api.saveManualAccount(account),
    onSuccess: async (result, account: ManualAccount) => {
      await invalidate(result)
      setSaved(account.name)
    },
  })

  const existing = initial.stableKey
  const submit = (event: FormEvent): void => {
    event.preventDefault()
    const refused = refuseAccount(draft)
    if (refused !== undefined) {
      setRefusal(refused)
      return
    }
    setRefusal(null)
    save.mutate(toManualAccount(draft, existing ?? deriveStableKey(draft.name, taken)))
  }

  const keep = (edit: PositionEdit): void => {
    setDraft((current) => ({
      ...current,
      positions:
        edit.index === undefined
          ? [...current.positions, edit.draft]
          : current.positions.map((p, i) => (i === edit.index ? edit.draft : p)),
    }))
    setRefusal(null)
    setEditing(null)
  }

  const drop = (index: number): void => {
    setDraft((current) => ({
      ...current,
      positions: current.positions.filter((_, i) => i !== index),
    }))
  }

  return (
    <>
      <Modal
        title={existing === undefined ? 'New account' : `Edit ${initial.name}`}
        onClose={onClose}
      >
        {saved !== null ? (
          <AfterHandEntry name={saved} onClose={onClose} />
        ) : (
          <>
            {switcher}
            <form onSubmit={submit} aria-label="Account">
          <fieldset>
            <label>
              Account name
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                required
              />
            </label>
            <EntityChoice
              label="Entity"
              placeholder="Choose…"
              value={draft.entityKey}
              required
              onChange={(entityKey) => setDraft({ ...draft, entityKey })}
            />
            <label>
              Account kind
              <select
                value={draft.kind}
                required
                onChange={(e) => setDraft({ ...draft, kind: e.target.value as ChosenAccountKind })}
              >
                <option value={UNCHOSEN}>Choose…</option>
                {ACCOUNT_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Currency
              <input
                value={draft.currency}
                onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                maxLength={3}
                required
              />
            </label>
            <label className="nw-check">
              <input
                type="checkbox"
                checked={draft.liquid}
                onChange={(e) => setDraft({ ...draft, liquid: e.target.checked })}
              />
              Liquid
            </label>
          </fieldset>

          <p className="nw-hint">
            {existing !== undefined ? (
              <>
                Filed under <code>{existing}</code>. Renaming the account leaves that alone:
                tonight&apos;s run finds this account by it, and moving it would leave the holdings
                behind under the old name.
              </>
            ) : draft.name.trim() === '' ? (
              <>Filed under a key worked out from the name.</>
            ) : (
              <>
                Filed under <code>{deriveStableKey(draft.name, taken)}</code>, worked out from the
                name.
              </>
            )}
          </p>

          {draft.positions.length === 0 ? (
            <p className="nw-none">
              No positions yet. An account with nothing in it counts for nothing, so add one before
              saving.
            </p>
          ) : (
            <ul className="nw-drafted">
              {draft.positions.map((position, index) => (
                <li key={`${position.symbol}-${index}`}>
                  <span className="nw-named">
                    <strong>{position.symbol.toUpperCase()}</strong>{' '}
                    {position.name === '' ? '' : `${position.name} · `}
                    {position.quantity}
                  </span>
                  <span className="nw-actions">
                    <Button onClick={() => setEditing({ index, draft: position })}>
                      Edit {position.symbol.toUpperCase()}
                    </Button>
                    <Button kind="danger" onClick={() => drop(index)}>
                      Remove {position.symbol.toUpperCase()}
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="nw-actions">
            <Button onClick={() => setEditing({ draft: emptyPosition(draft.currency) })}>
              Add position
            </Button>
          </div>

          {refusal !== null && <p className="nw-error">{refusal}</p>}
          {save.error && <p className="nw-error">{save.error.message}</p>}

          <div className="nw-actions">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" kind="primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save account'}
            </Button>
          </div>
            </form>
          </>
        )}
      </Modal>

      {/*
        Beside the account's dialog rather than inside it: a dialog's close event
        does not bubble, but React hands it to the components above it anyway, so
        a position dismissed from within would take the account with it.
      */}
      {editing && (
        <PositionDialog
          edit={editing}
          accountKind={draft.kind}
          onKeep={keep}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  )
}

/**
 * One position, entered on its own and kept on its own.
 *
 * It is a separate form from the account's, which is what stops a position
 * nobody finished from holding the account hostage: the boxes it insists on are
 * its own, and a dialog dismissed takes its half-typed holding with it rather
 * than leaving one behind that cannot be reached or removed.
 */
function PositionDialog({
  edit,
  accountKind,
  onKeep,
  onClose,
}: {
  edit: PositionEdit
  accountKind: ChosenAccountKind
  onKeep: (edit: PositionEdit) => void
  onClose: () => void
}): ReactElement {
  const [draft, setDraft] = useState<PositionDraft>(edit.draft)
  const [refusal, setRefusal] = useState<string | null>(null)

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    const refused = refusePosition(draft, accountKind)
    if (refused !== undefined) {
      setRefusal(refused)
      return
    }
    onKeep({ ...edit, draft })
  }

  return (
    <Modal title={edit.index === undefined ? 'Add position' : 'Edit position'} onClose={onClose}>
      <form onSubmit={submit} aria-label="Position">
        <fieldset>
          <label>
            Instrument kind
            <select
              value={draft.kind}
              required
              onChange={(e) => setDraft({ ...draft, kind: e.target.value as ChosenInstrumentKind })}
            >
              <option value={UNCHOSEN}>Choose…</option>
              {INSTRUMENT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </select>
          </label>
          <label>
            Symbol
            <input
              value={draft.symbol}
              onChange={(e) => setDraft({ ...draft, symbol: e.target.value })}
              required
            />
          </label>
          <label>
            Instrument name
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <label>
            Currency
            <input
              value={draft.currency}
              onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              maxLength={3}
              required
            />
          </label>
          <label>
            Quantity
            <input
              value={draft.quantity}
              onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
              inputMode="decimal"
              required
            />
          </label>
          <label>
            Cost basis
            <input
              value={draft.costBasisMajor}
              onChange={(e) => setDraft({ ...draft, costBasisMajor: e.target.value })}
              inputMode="decimal"
            />
          </label>
          <label>
            Cost basis paid in
            <input
              value={draft.costBasisCurrency}
              onChange={(e) => setDraft({ ...draft, costBasisCurrency: e.target.value })}
              maxLength={3}
              placeholder={draft.currency.trim().toUpperCase()}
            />
          </label>
          <label>
            Held since
            <input
              type="date"
              value={draft.asOf}
              onChange={(e) => setDraft({ ...draft, asOf: e.target.value })}
            />
          </label>
          <label>
            ISIN
            <input
              value={draft.isin}
              onChange={(e) => setDraft({ ...draft, isin: e.target.value })}
            />
          </label>
        </fieldset>

        <p className="nw-hint">
          The name is what the dashboard calls this holding wherever it appears. Left blank it
          becomes the symbol.
        </p>

        {refusal !== null && <p className="nw-error">{refusal}</p>}

        <div className="nw-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" kind="primary">
            {edit.index === undefined ? 'Add position' : 'Keep changes'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

interface ValuationDraftState {
  date: string
  sharesHeld: string
  pricePerShareMajor: string
  round: string
  confidence: Confidence
  haircutPercent: string
  source: string
}

/**
 * What a holding was worth on a day, asked of the holding.
 *
 * The symbol, the currency and whose it is come from the row this was opened
 * from rather than from anyone's memory of them. Retyping them was the only way
 * to record a valuation before, and a symbol typed a second time is a second
 * instrument: the event lands on a row nothing holds, where it prices nothing
 * and says so nowhere.
 */
function ValuationDialog({
  target,
  onClose,
}: {
  target: ValuationTarget
  onClose: () => void
}): ReactElement {
  const [draft, setDraft] = useState<ValuationDraftState>(() => ({
    date: today(),
    sharesHeld: target.quantity,
    pricePerShareMajor: '',
    round: '',
    confidence: 'medium',
    haircutPercent: '0',
    source: '',
  }))
  const [refusal, setRefusal] = useState<string | null>(null)
  const invalidate = useInvalidateMoney()

  const save = useMutation({
    mutationFn: api.saveValuation,
    onSuccess: async (result) => {
      await invalidate(result)
      onClose()
    },
  })

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    try {
      save.mutate({
        entityKey: target.entityKey === '' ? undefined : target.entityKey,
        instrument: target.instrument,
        date: draft.date,
        sharesHeld: draft.sharesHeld.trim(),
        pricePerShareMinor: minorFromMajorString(draft.pricePerShareMajor.trim()),
        round: draft.round.trim() === '' ? undefined : draft.round.trim(),
        confidence: draft.confidence,
        // A percentage entered as a decimal string is basis points once scaled by 100.
        haircutBp: minorFromMajorString(draft.haircutPercent.trim() || '0'),
        source: draft.source.trim(),
      })
      setRefusal(null)
    } catch (error) {
      setRefusal((error as Error).message)
    }
  }

  return (
    <Modal title={`Value ${target.instrument.symbol}`} onClose={onClose}>
      <form onSubmit={submit} aria-label="Valuation">
        <p className="nw-hint">
          {target.instrument.name}, held in {target.accountName} and priced in{' '}
          {target.instrument.currency}. A holding is valued from the latest event on or before the
          date being looked at, so a new round never reprices the history behind it. Changing the
          count here corrects it for every date; to say that some of it left, reduce the holding
          instead.
        </p>
        <fieldset>
          <label>
            Date
            <input
              type="date"
              value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              required
            />
          </label>
          <label>
            Price per share
            <input
              value={draft.pricePerShareMajor}
              onChange={(e) => setDraft({ ...draft, pricePerShareMajor: e.target.value })}
              inputMode="decimal"
              required
            />
          </label>
          <label>
            Shares held
            <input
              value={draft.sharesHeld}
              onChange={(e) => setDraft({ ...draft, sharesHeld: e.target.value })}
              inputMode="decimal"
              required
            />
          </label>
          <label>
            Round
            <input
              value={draft.round}
              onChange={(e) => setDraft({ ...draft, round: e.target.value })}
            />
          </label>
          <label>
            Confidence
            <select
              value={draft.confidence}
              onChange={(e) => setDraft({ ...draft, confidence: e.target.value as Confidence })}
            >
              {CONFIDENCES.map((confidence) => (
                <option key={confidence} value={confidence}>
                  {confidence}
                </option>
              ))}
            </select>
          </label>
          <label>
            Haircut %
            <input
              value={draft.haircutPercent}
              onChange={(e) => setDraft({ ...draft, haircutPercent: e.target.value })}
              inputMode="decimal"
            />
          </label>
          <label>
            Source note
            <input
              value={draft.source}
              onChange={(e) => setDraft({ ...draft, source: e.target.value })}
              required
            />
          </label>
        </fieldset>

        {refusal !== null && <p className="nw-error">{refusal}</p>}
        {save.error && <p className="nw-error">{save.error.message}</p>}

        <div className="nw-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" kind="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save valuation'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/** A private holding whose history is being read. */
interface HistoryTarget {
  instrumentId: number
  symbol: string
  name: string
  unitLabel: string
  currency: string
}

/**
 * Everything that happened to one holding, newest first: when it started, each
 * valuation or round, each reduction and each corrected count, with the count
 * it left behind. The newest line reads what the table does.
 *
 * A reduction can be taken back from here, asked twice: it rewrites every
 * stored day from its date on, and the confirmation says so.
 */
function HistoryDialog({ target, onClose }: { target: HistoryTarget; onClose: () => void }): ReactElement {
  const history = useQuery({
    queryKey: ['holdings', 'history', target.instrumentId],
    queryFn: () => api.holdingHistory(target.instrumentId),
  })
  const [undoing, setUndoing] = useState<string | null>(null)
  const invalidate = useInvalidateMoney()
  const undo = useMutation({
    mutationFn: (date: string) => api.deleteDisposal(target.instrumentId, date),
    onSuccess: async (result) => {
      setUndoing(null)
      await invalidate(result)
    },
  })

  const events = [...(history.data?.events ?? [])].reverse()

  return (
    <Modal title={`History of ${target.symbol}`} onClose={onClose}>
      <p className="nw-hint">
        {target.name}. The count after each line is what the holding is understood to have held on
        that date, with every correction applied.
      </p>
      {history.isPending && <p className="nw-hint">Loading…</p>}
      {history.error && <p className="nw-error">{history.error.message}</p>}
      {history.data !== undefined && events.length === 0 && (
        <p className="nw-hint">Nothing is recorded for this holding yet.</p>
      )}
      {events.length > 0 && (
        <ol className="nw-drafted nw-history" aria-label={`History of ${target.symbol}`}>
          {events.map((event, index) => (
            <li key={`${event.kind}-${event.date}-${index}`}>
              <span>
                <strong>{event.date}</strong> · {describeEvent(event, target)}
                <br />
                <small>
                  {formatQuantity(event.unitsAfter)} {target.unitLabel}s after
                </small>
              </span>
              {event.kind === 'reduced' &&
                (undoing === event.date ? (
                  <span className="nw-actions">
                    <small>Take back this reduction? Every day from {event.date} is restored.</small>
                    <Button kind="danger" disabled={undo.isPending} onClick={() => undo.mutate(event.date)}>
                      {undo.isPending ? 'Undoing…' : 'Undo reduction'}
                    </Button>
                    <Button onClick={() => setUndoing(null)}>Keep it</Button>
                  </span>
                ) : (
                  <Button aria-label={`Undo the ${event.date} reduction`} onClick={() => setUndoing(event.date)}>
                    Undo
                  </Button>
                ))}
            </li>
          ))}
        </ol>
      )}
      {undo.error && <p className="nw-error">{undo.error.message}</p>}
      <div className="nw-actions">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  )
}

function describeEvent(event: HoldingEvent, target: HistoryTarget): string {
  switch (event.kind) {
    case 'started':
      return `Held from ${formatQuantity(event.units)} ${target.unitLabel}s`
    case 'valued':
      return [
        event.round === null ? `Valued (${event.valuationKind})` : `${event.round} (${event.valuationKind})`,
        `${formatMinor(event.pricePerUnitMinor, event.currency)} per ${target.unitLabel}`,
        event.haircutBp > 0 ? `${event.haircutBp / 100}% haircut` : null,
        event.source,
      ]
        .filter((part): part is string => part !== null)
        .join(' · ')
    case 'reduced':
      return [
        `Reduced by ${formatQuantity(event.units)} ${target.unitLabel}s`,
        event.proceedsMinor === undefined ? null : `for ${formatMinor(event.proceedsMinor, target.currency)}`,
        event.note ?? null,
      ]
        .filter((part): part is string => part !== null)
        .join(' · ')
    case 'corrected':
      return `Count corrected from ${formatQuantity(event.from)} to ${formatQuantity(event.to)} (${
        event.via === 'value-dialog' ? 'in a valuation' : 'on the holding'
      })`
  }
}

/** A holding on screen, with everything a disposal of it needs already known. */
interface DisposalTarget {
  instrumentId: number
  symbol: string
  name: string
  unitLabel: string
  currency: string
  /** What is held today, which is the most that can leave. */
  units: string
  heldSince?: string
  disposals: readonly Disposal[]
}

interface DisposalDraftState {
  date: string
  units: string
  proceedsMajor: string
  note: string
}

/**
 * Units leaving a holding, which is a different thing from the count having
 * been wrong.
 *
 * Correcting the count belongs in the valuation beside it and speaks for every
 * date; this speaks only for the dates from its own on. The dialog says so out
 * loud, because the two are one sentence apart in English and the one that gets
 * it wrong quietly restates years of history.
 */
function DisposalDialog({
  target,
  onClose,
}: {
  target: DisposalTarget
  onClose: () => void
}): ReactElement {
  const [draft, setDraft] = useState<DisposalDraftState>(() => ({
    date: today(),
    units: '',
    proceedsMajor: '',
    note: '',
  }))
  const [refusal, setRefusal] = useState<string | null>(null)
  const invalidate = useInvalidateMoney()

  const typed = draft.units.trim()
  const leaving = isQuantity(typed) ? subQuantity(target.units, typed) : null

  const save = useMutation({
    mutationFn: (units: string) =>
      api.recordDisposal(target.instrumentId, {
        date: draft.date,
        units,
        ...(draft.proceedsMajor.trim() === ''
          ? {}
          : { proceedsMinor: minorFromMajorString(draft.proceedsMajor.trim()) }),
        ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
      }),
    onSuccess: async (result) => {
      await invalidate(result)
      onClose()
    },
  })

  const remove = useMutation({
    mutationFn: (date: string) => api.deleteDisposal(target.instrumentId, date),
    onSuccess: invalidate,
  })

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    const units = draft.units.trim()
    if (!isQuantity(units)) {
      setRefusal('how many units left, as a decimal number')
      return
    }
    try {
      minorFromMajorString(draft.proceedsMajor.trim() || '0')
    } catch (error) {
      setRefusal(`proceeds: ${(error as Error).message}`)
      return
    }
    setRefusal(null)
    save.mutate(units)
  }

  return (
    <Modal title={`Reduce ${target.symbol}`} onClose={onClose}>
      <form onSubmit={submit} aria-label="Disposal">
        <p className="nw-hint">
          {target.name}, {formatQuantity(target.units)} {target.unitLabel}s held today. Every date
          before the one below keeps the count it had — this records what left, not that the count
          on file was wrong. Correcting a count that was never right belongs in a valuation.
        </p>
        <fieldset>
          <label>
            Date
            <input
              type="date"
              value={draft.date}
              min={target.heldSince}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              required
            />
          </label>
          <label>
            Units disposed of
            <input
              value={draft.units}
              onChange={(e) => setDraft({ ...draft, units: e.target.value })}
              inputMode="decimal"
              required
            />
          </label>
          <label>
            Proceeds
            <input
              value={draft.proceedsMajor}
              onChange={(e) => setDraft({ ...draft, proceedsMajor: e.target.value })}
              inputMode="decimal"
            />
          </label>
          <label>
            Note
            <input
              value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            />
          </label>
        </fieldset>

        {/*
          What the holding goes from and to, before it is committed. What a
          disposal leaves behind depends on the count already on file, and a
          count corrected to the post-sale figure first would be reduced twice
          — visibly here, and nowhere else.
        */}
        <p className="nw-hint">
          {leaving === null ? (
            <>
              Proceeds are what the units fetched. Left blank, the total simply falls on that date
              with nothing to say where the money went.
            </>
          ) : (
            <>
              {target.symbol} goes from <strong>{formatQuantity(target.units)}</strong> to{' '}
              <strong>{formatQuantity(leaving)}</strong> {target.unitLabel}s on {draft.date}, and
              stays at {formatQuantity(target.units)} on every date before it.
            </>
          )}
        </p>

        {target.disposals.length > 0 && (
          <ul className="nw-drafted">
            {target.disposals.map((disposal) => (
              <li key={disposal.date}>
                <span className="nw-named">
                  <strong>{disposal.date}</strong> · {formatQuantity(disposal.units)}{' '}
                  {target.unitLabel}s
                  {disposal.proceedsMinor === undefined
                    ? ''
                    : ` · ${formatMinor(disposal.proceedsMinor, target.currency)}`}
                </span>
                <Button
                  kind="danger"
                  disabled={remove.isPending}
                  aria-label={`Take back the ${disposal.date} disposal`}
                  onClick={() => remove.mutate(disposal.date)}
                >
                  Take back
                </Button>
              </li>
            ))}
          </ul>
        )}

        {refusal !== null && <p className="nw-error">{refusal}</p>}
        {save.error && <p className="nw-error">{save.error.message}</p>}
        {remove.error && <p className="nw-error">{remove.error.message}</p>}

        <div className="nw-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" kind="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Record disposal'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function ValuationHistory(): ReactElement {
  const invalidate = useInvalidateMoney()
  const events = useQuery({ queryKey: ['valuations'], queryFn: () => api.valuations() })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteValuation(id),
    onSuccess: invalidate,
  })

  return (
    <Card>
      <CardHead
        title="Valuation history"
        lede="Every valuation ever entered, newest first. A new one is added from the position it values, above."
      />

      {events.error && <p className="nw-error">Could not load valuations: {events.error.message}</p>}
      {remove.error && <p className="nw-error">{remove.error.message}</p>}

      {events.data && (
        <DataTable columns={VALUATION_COLUMNS} label="Every valuation ever entered">
            {events.data.events.map((event) => (
              <tr key={event.id}>
                <td>{event.date}</td>
                <td>{event.symbol}</td>
                <td>{event.round ?? '—'}</td>
                <td className="nw-num">{formatQuantity(event.sharesHeld)}</td>
                <td className="nw-num">
                  {formatMinor(event.pricePerShareMinor, event.currency, {
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td className="nw-num">{event.haircutBp / 100}%</td>
                <td>{event.source}</td>
                <td>
                  <Button
                    kind="danger"
                    aria-label={`Delete the ${event.date} ${event.symbol} valuation`}
                    onClick={() => remove.mutate(event.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
            {events.data.events.length === 0 && (
              <tr>
                <td colSpan={VALUATION_COLUMNS.length}>No valuation events recorded.</td>
              </tr>
            )}
        </DataTable>
      )}
    </Card>
  )
}
